import { useEffect, useState } from "react";
import { Copy, Gift, MessageCircle, Mail, Users, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";

const STATUS = { signed_up: ["Iscritto", "bg-slate-100 text-slate-600"], rewarded: ["Ha attivato Pro", "bg-emerald-50 text-emerald-700"] };

export default function Referral() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/referral/me").then(({ data }) => setD(data)).catch(() => setErr(true));
  }, []);
  if (err) return <p className="text-slate-500" data-testid="referral-error">Impossibile caricare gli inviti. Riprova tra poco.</p>;
  if (!d) return <div className="h-40 rounded-3xl bg-slate-100 animate-pulse" />;

  const link = `${window.location.origin}/?ref=${d.code}`;
  const msg = `Uso Burocrazy per scadenze, bollette e pratiche: fai una foto e ti dice cosa fare. Provalo gratis: ${link}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiato");
  };

  return (
    <div data-testid="referral-page">
      <PageHeader eyebrow="Invita amici" title="Regala ordine, guadagna mesi Pro" desc="Condividi il tuo link. Ogni amico che si iscrive e attiva il piano Pro ti regala 1 mese di Pro gratis." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-[#0B0F19] text-white p-8 relative overflow-hidden noise">
          <Gift className="h-8 w-8 text-[#E07A5F]" />
          <p className="mt-6 text-sm text-slate-300">Il tuo link personale</p>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <Input readOnly value={link} className="bg-white/10 border-white/20 text-white h-12" data-testid="referral-link-input" />
            <Button onClick={copy} className="h-12 rounded-full bg-[#E07A5F] hover:bg-[#d06a50] px-6" data-testid="referral-copy-btn"><Copy className="h-4 w-4 mr-2" /> Copia</Button>
          </div>
          <p className="mt-3 text-xs text-slate-400">Codice: <span className="font-mono text-white" data-testid="referral-code">{d.code}</span></p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button asChild variant="outline" className="rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">
              <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer" data-testid="referral-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</a>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">
              <a href={`mailto:?subject=${encodeURIComponent("Ti consiglio Burocrazy")}&body=${encodeURIComponent(msg)}`} data-testid="referral-email-btn"><Mail className="h-4 w-4 mr-2" /> Email</a>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <div className="rounded-3xl border bg-white p-6" data-testid="referral-invited"><Users className="h-5 w-5 text-[#1E3A8A]" /><p className="mt-3 text-3xl font-display font-semibold">{d.invited}</p><p className="text-sm text-slate-500">Amici iscritti</p></div>
          <div className="rounded-3xl border bg-white p-6" data-testid="referral-paid"><Crown className="h-5 w-5 text-[#E07A5F]" /><p className="mt-3 text-3xl font-display font-semibold">{d.paid}</p><p className="text-sm text-slate-500">Mesi Pro guadagnati</p></div>
        </div>
      </div>
      <div className="mt-6 rounded-3xl border bg-white p-6">
        <h3 className="font-medium">I tuoi inviti</h3>
        {d.items.length === 0 && <p className="mt-2 text-sm text-slate-500" data-testid="referral-empty">Nessun invito ancora. Inizia da chi ti chiede sempre aiuto con le pratiche.</p>}
        <ul className="divide-y">
          {d.items.map((r, i) => (
            <li key={i} className="py-3 flex items-center justify-between gap-3" data-testid={`referral-item-${i}`}>
              <div><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">Iscritto il {fmtDate(r.created_at)}{r.reward ? ` · Premio: ${r.reward}` : ""}</p></div>
              <span className={`text-xs rounded-full px-2.5 py-1 ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-xs text-slate-500">Il premio scatta una sola volta per ogni amico, quando attiva il primo abbonamento Pro. Se sei già abbonato ricevi uno sconto di 9,99 € sul rinnovo successivo.</p>
    </div>
  );
}
